import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from "primereact/toast";
import ComplaintService from '../../services/complaint/ComplaintService';
import AppLoading from '../app-loading/app-loading';
import { ComplaintActionModel } from '../../models/complaints/complaint-model';
import InternalComplaintActionDataTable from './InternalComplaintActionDataTable';
import InternalComplaintActionSearch from './InternalComplaintActionSearch';

const InternalComplaintActionMain: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [filteredActions, setFilteredActions] = useState<ComplaintActionModel[]>([]);
  const [actionList, setActionList] = useState<ComplaintActionModel[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<any>({});

  useEffect(() => {
    const loadComplaintActions = async () => {
      setLoading(true);
      try {
        const response = await ComplaintService.GetAllInternalComplaintAction();
        let initialFiltered = [...response];
        
        // Date filter
        if (filters.startDate && filters.endDate) {
          initialFiltered = initialFiltered.filter(action => {
            const actionDate = new Date(action.actionDate || '');
            return actionDate >= filters.startDate && 
                   actionDate <= filters.endDate;
          });
        }

        // Keyword filter
        if (filters.searchKeyword) {
          const searchTerm = filters.searchKeyword.toLowerCase();
          initialFiltered = initialFiltered.filter(action => {
            return Object.values(action).some(value => 
              value?.toString().toLowerCase().includes(searchTerm)
            );
          });
        }

        setActionList(response);
        setFilteredActions(initialFiltered);
        setTotalRecords(initialFiltered.length);
      } catch (error) {
        console.error('Error loading complaint actions:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load complaint actions',
          life: 3000
        });
        setActionList([]);
        setFilteredActions([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };

    loadComplaintActions();
  }, [pageNo, pageSize, filters]);

  const handleSearch = (searchFilters: any) => {
    setPageNo(1);
    setFilters(searchFilters);
  };

  const handlePageChange = (event: any) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPageNo(newPageNumber);
    setPageSize(event.rows);
  };

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <Toast ref={toast} position="top-center" />
        <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
          <div className="col-12">
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
              <h5 className="ml-1 mb-0">{t("View All Internal Complaint Actions")}</h5>
            </div>
            
            <InternalComplaintActionSearch
              onSearch={handleSearch}
              setLoading={setLoading}
            />
          </div>
          
          <InternalComplaintActionDataTable
            data={filteredActions}
            pageNo={pageNo}
            pageSize={pageSize}
            handlepagechange={handlePageChange}
            totalRecordsCount={totalRecords}
          />
        </div>
        {loading && <AppLoading />}
      </Suspense>
    </>
  );
};

export default InternalComplaintActionMain;